class EMROICalculator {
  constructor() {
    this.products = [];
    this.currentCategory = "preset";
    this.chart = null;
    this.selectedProducts = new Set(); // Track selected product IDs

    // 从data属性读取机器价格和其他参数
    const roiCalculatorElement = document.querySelector("[data-machine-price]");
    this.machinePrice = roiCalculatorElement
      ? parseFloat(roiCalculatorElement.dataset.machinePrice)
      : 3099;
    this.machineDepreciationRate = roiCalculatorElement
      ? parseFloat(roiCalculatorElement.dataset.machineDepreciationRate || 2.5)
      : 2.5;
    this.overheadRate = roiCalculatorElement
      ? parseFloat(roiCalculatorElement.dataset.overheadRate || 15)
      : 15;
    this.markupRate = roiCalculatorElement
      ? parseFloat(roiCalculatorElement.dataset.markupRate || 50)
      : 50;
    this.currencySymbol = roiCalculatorElement
      ? (roiCalculatorElement.dataset.currencySymbol || '$')
      : '$';

    // 移动端滚动相关变量
    this.isMobile = false;
    this.handleTouchStart = null;
    this.handleTouchMove = null;
    this.handleTouchEnd = null;

    // 移动端导航相关变量
    this.currentMobileProductIndex = 0;
    this.mobileVisibleProducts = [];

    this.init();
  }

  init() {
    // 首先检测移动设备
    this.isMobile = window.innerWidth <= 768;

    this.bindEvents();
    this.initializeChart();
    this.loadPresetProducts();
    this.initializeSliders();
    this.initializeSettingsSliders();

    // 延迟初始化移动端滚动，确保DOM完全加载
    setTimeout(() => {
      this.initializeMobileScrolling();
    }, 100);

    this.applyProductSelection(); // Apply initial selection
    this.syncCheckboxStates(); // Sync checkbox states with selection
    this.updateCalculations();
  }

  bindEvents() {
    // Category tab switching
    document.querySelectorAll(".em-category-tab").forEach((tab) => {
      tab.addEventListener("click", (e) => this.switchCategory(e));
    });

    // Slider events
    document.addEventListener("input", (e) => {
      if (e.target.classList.contains("em-slider")) {
        this.handleSliderChange(e.target);
      }
    });

    // Settings slider events
    document.addEventListener("input", (e) => {
      if (e.target.classList.contains("em-setting-slider")) {
        this.handleSettingChange(e.target);
      }
    });

    // Custom product form
    const addCustomBtn = document.getElementById("em-add-custom-product");
    if (addCustomBtn) {
      addCustomBtn.addEventListener("click", () => this.addCustomProduct());
    }

    // Form inputs for custom products
    document.querySelectorAll("#em-custom-products input").forEach((input) => {
      input.addEventListener("keypress", (e) => {
        if (e.key === "Enter") {
          this.addCustomProduct();
        }
      });
    });

    // Mobile navigation buttons
    const prevBtn = document.getElementById("em-products-prev-btn");
    const nextBtn = document.getElementById("em-products-next-btn");
    if (prevBtn)
      prevBtn.addEventListener("click", () => this.showPreviousMobileProduct());
    if (nextBtn)
      nextBtn.addEventListener("click", () => this.showNextMobileProduct());

    // Product selection events
    this.bindProductSelectionEvents();
  }

  bindProductSelectionEvents() {
    // Individual product checkbox events
    document.querySelectorAll(".em-product-checkbox").forEach((checkbox) => {
      checkbox.addEventListener("change", (e) =>
        this.handleProductSelection(e.target),
      );
    });

    // Select all button
    const selectAllBtn = document.getElementById("em-select-all-products");
    if (selectAllBtn) {
      selectAllBtn.addEventListener("click", () => this.selectAllProducts());
    }

    // Deselect all button
    const deselectAllBtn = document.getElementById("em-deselect-all-products");
    if (deselectAllBtn) {
      deselectAllBtn.addEventListener("click", () =>
        this.deselectAllProducts(),
      );
    }

    // Apply selection button
    const applyBtn = document.getElementById("em-apply-product-selection");
    if (applyBtn) {
      applyBtn.addEventListener("click", () => this.applyProductSelection());
    }

    // Cancel selection button
    const cancelBtn = document.getElementById("em-cancel-product-selection");
    if (cancelBtn) {
      cancelBtn.addEventListener("click", () =>
        this.closeProductSelectionModal(),
      );
    }

    // Modal trigger button
    const openBtn = document.getElementById("em-open-product-selection");
    if (openBtn) {
      openBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        this.openProductSelectionModal();
      });
    }

    // Modal close button
    const closeBtn = document.getElementById("em-close-product-selection");
    if (closeBtn) {
      closeBtn.addEventListener("click", () =>
        this.closeProductSelectionModal(),
      );
    }

    // Modal overlay click to close
    const overlay = document.getElementById("em-modal-overlay");
    if (overlay) {
      overlay.addEventListener("click", () =>
        this.closeProductSelectionModal(),
      );
    }

    // Prevent modal content clicks from closing modal
    const modalContent = document.querySelector(".em-modal-content");
    if (modalContent) {
      modalContent.addEventListener("click", (e) => e.stopPropagation());
    }
  }

  switchCategory(event) {
    const tab = event.currentTarget;
    const category = tab.dataset.category;

    // Update active tab
    document
      .querySelectorAll(".em-category-tab")
      .forEach((t) => t.classList.remove("active"));
    tab.classList.add("active");

    // Show/hide sections
    document
      .querySelectorAll(
        ".em-preset-products-section, .em-custom-products-section",
      )
      .forEach((section) => {
        section.classList.remove("active");
        section.style.display = "none";
      });

    const targetSection = document.getElementById(`em-${category}-products`);
    if (targetSection) {
      targetSection.classList.add("active");
      targetSection.style.display = "block";
    }

    this.currentCategory = category;

    // Apply product selection when switching to preset view
    if (category === "preset") {
      this.applyProductSelection();
    }

    // 重新初始化滚动行为，确保新显示的内容正确配置
    setTimeout(() => {
      this.setupScrollBehavior();
    }, 100);
  }

  loadPresetProducts() {
    // Load products from DOM data
    const productCards = document.querySelectorAll(
      ".em-product-card-roi[data-product-data]",
    );

    // 根据设备类型决定默认选择的产品数量
    const defaultSelectionCount = this.isMobile ? 1 : 3;

    productCards.forEach((card, index) => {
      try {
        const productData = JSON.parse(card.dataset.productData);
        const productId = card.dataset.productId;

        const product = {
          id: `preset-${productId}`,
          name: productData.name,
          material_cost: parseFloat(productData.price) || 0, // 材料成本
          labor: parseFloat(productData.cost) || 0, // 人工成本
          sales: parseInt(productData.sales) || 0,
          element: card,
          type: "preset",
        };

        this.products.push(product);
        this.bindProductEvents(card, product);

        // 根据设备类型选择默认产品数量
        if (index < defaultSelectionCount) {
          this.selectedProducts.add(productId);
          this.mobileVisibleProducts.push(productId);
          card.style.display = "block";
        } else {
          card.style.display = "none";
        }
      } catch (e) {
        console.error("Error parsing product data:", e);
      }
    });

    // 初始化移动端导航
    if (this.isMobile) {
      setTimeout(() => {
        this.toggleMobileNavigation();
        this.updateMobileNavigation();
      }, 100);
    }

    console.log(
      `已加载 ${this.products.length} 个产品，${this.isMobile ? "移动端" : "桌面端"}默认选择 ${defaultSelectionCount} 个产品`,
    );
  }

  handleProductSelection(checkbox) {
    const productId = checkbox.dataset.productId;

    if (checkbox.checked) {
      this.selectedProducts.add(productId);
      // Show the corresponding product card
      const productCard = document.querySelector(
        `.em-product-card-roi[data-product-id="${productId}"], .em-product-card-roi[data-product-id="preset-${productId}"]`,
      );
      if (productCard) {
        productCard.style.display = "block";
      }
    } else {
      this.selectedProducts.delete(productId);
      // Hide the corresponding product card
      const productCard = document.querySelector(
        `.em-product-card-roi[data-product-id="${productId}"], .em-product-card-roi[data-product-id="preset-${productId}"]`,
      );
      if (productCard) {
        productCard.style.display = "none";
      }
    }

    this.updateSelectionUI();
    this.updateCalculations();
  }

  selectAllProducts() {
    document.querySelectorAll(".em-product-checkbox").forEach((checkbox) => {
      checkbox.checked = true;
      const productId = checkbox.dataset.productId;
      this.selectedProducts.add(productId);

      // Show the corresponding product card
      const productCard = document.querySelector(
        `.em-product-card-roi[data-product-id="${productId}"], .em-product-card-roi[data-product-id="preset-${productId}"]`,
      );
      if (productCard) {
        productCard.style.display = "block";
      }
    });

    this.updateSelectionUI();
    this.updateCalculations();
  }

  deselectAllProducts() {
    document.querySelectorAll(".em-product-checkbox").forEach((checkbox) => {
      checkbox.checked = false;
      const productId = checkbox.dataset.productId;
      this.selectedProducts.delete(productId);

      // Hide the corresponding product card
      const productCard = document.querySelector(
        `.em-product-card-roi[data-product-id="${productId}"], .em-product-card-roi[data-product-id="preset-${productId}"]`,
      );
      if (productCard) {
        productCard.style.display = "none";
      }
    });

    this.updateSelectionUI();
    this.updateCalculations();
  }

  applyProductSelection() {
    // Filter products based on selection
    const selectedProductIds = Array.from(this.selectedProducts);
    const visibleProducts = this.products.filter((product) => {
      const productId = product.id.replace("preset-", "");
      return selectedProductIds.includes(productId);
    });

    // Hide/show product cards based on selection
    this.products.forEach((product) => {
      const productId = product.id.replace("preset-", "");
      if (this.selectedProducts.has(productId)) {
        product.element.style.display = "block";
      } else {
        product.element.style.display = "none";
      }
    });

    // Update mobile visible products list
    this.mobileVisibleProducts = Array.from(this.selectedProducts).map((id) =>
      id.replace("preset-", ""),
    );
    this.currentMobileProductIndex = 0;

    // Update calculations with only selected products
    this.updateCalculations();

    // Close modal after applying selection
    this.closeProductSelectionModal();

    // Update mobile navigation display
    if (this.isMobile) {
      setTimeout(() => {
        this.updateMobileProductDisplay();
        this.toggleMobileNavigation();
      }, 200);
    } else {
      setTimeout(() => {
        this.setupScrollBehavior();
      }, 200); // 增加延迟确保DOM更新完成
    }
  }

  updateSelectionUI() {
    // Update visual feedback for selection count
    const selectedCount = this.selectedProducts.size;
    const totalCount = document.querySelectorAll(".em-product-checkbox").length;

    // You can add visual feedback here if needed
    console.log(`Selected ${selectedCount} of ${totalCount} products`);
  }

  openProductSelectionModal() {
    const modal = document.getElementById("em-product-selection-modal");
    if (modal) {
      modal.classList.add("active");
      document.body.style.overflow = "hidden"; // Prevent background scrolling
    }
  }

  closeProductSelectionModal() {
    const modal = document.getElementById("em-product-selection-modal");
    if (modal) {
      // Add closing animation class for mobile
      if (this.isMobile) {
        modal.classList.add("closing");
        setTimeout(() => {
          modal.classList.remove("active");
          modal.classList.remove("closing");
          document.body.style.overflow = ""; // Restore scrolling
        }, 300);
      } else {
        modal.classList.remove("active");
        document.body.style.overflow = ""; // Restore scrolling
      }
    }
  }

  syncCheckboxStates() {
    // Sync checkbox states in the modal with the current selection
    document.querySelectorAll(".em-product-checkbox").forEach((checkbox) => {
      const productId = checkbox.dataset.productId;
      checkbox.checked = this.selectedProducts.has(productId);
    });
  }

  showPreviousMobileProduct() {
    if (this.currentMobileProductIndex > 0) {
      this.currentMobileProductIndex--;
      this.updateMobileProductDisplay();
    }
  }

  showNextMobileProduct() {
    if (
      this.currentMobileProductIndex <
      this.mobileVisibleProducts.length - 1
    ) {
      this.currentMobileProductIndex++;
      this.updateMobileProductDisplay();
    }
  }

  updateMobileProductDisplay() {
    // Hide all product cards
    document.querySelectorAll(".em-product-card-roi").forEach((card) => {
      card.removeAttribute("data-mobile-visible");
    });

    // Show only current product
    if (this.mobileVisibleProducts[this.currentMobileProductIndex]) {
      const currentProductId =
        this.mobileVisibleProducts[this.currentMobileProductIndex];
      const currentCard = document.querySelector(
        `.em-product-card-roi[data-product-id="${currentProductId}"]`,
      );
      if (currentCard) {
        currentCard.setAttribute("data-mobile-visible", "true");
      }
    }

    // Update counter and button states
    this.updateMobileNavigation();
  }

  updateMobileNavigation() {
    const prevBtn = document.getElementById("em-products-prev-btn");
    const nextBtn = document.getElementById("em-products-next-btn");
    const currentIndex = document.getElementById("em-current-product-index");
    const totalProducts = document.getElementById("em-total-products");

    if (prevBtn) {
      prevBtn.disabled = this.currentMobileProductIndex === 0;
    }
    if (nextBtn) {
      nextBtn.disabled =
        this.currentMobileProductIndex >= this.mobileVisibleProducts.length - 1;
    }
    if (currentIndex) {
      currentIndex.textContent = this.currentMobileProductIndex + 1;
    }
    if (totalProducts) {
      totalProducts.textContent = this.mobileVisibleProducts.length;
    }
  }

  toggleMobileNavigation() {
    const mobileNav = document.getElementById("em-products-mobile-nav");
    if (mobileNav) {
      if (this.isMobile && this.mobileVisibleProducts.length > 0) {
        mobileNav.classList.add("active");
      } else {
        mobileNav.classList.remove("active");
      }
    }
  }

  bindProductEvents(card, product) {
    // Bind slider events for this product
    const sliders = card.querySelectorAll(".em-slider");
    sliders.forEach((slider) => {
      // Initialize progress bar for this slider
      this.updateSliderProgress(slider);

      slider.addEventListener("input", () => {
        this.updateProductFromSlider(product, slider);
        this.updateCalculations();
      });
    });
  }

  handleSettingChange(slider) {
    const setting = slider.dataset.setting;
    const value = parseFloat(slider.value);
    const valueDisplay = slider
      .closest(".em-slider-container")
      .querySelector(".em-slider-value");

    if (valueDisplay) {
      // Format value based on setting type
      let displayValue = value;
      if (setting === "machine_price") {
        displayValue = this.currencySymbol + value.toLocaleString();
      } else if (setting === "overhead_rate") {
        displayValue = this.currencySymbol + value;
      } else {
        displayValue = value + "%";
      }
      valueDisplay.textContent = displayValue;
    }

    // Update the corresponding property
    switch (setting) {
      case "machine_price":
        this.machinePrice = value;
        break;
      case "machine_depreciation_rate":
        this.machineDepreciationRate = value;
        break;
      case "overhead_rate":
        this.overheadRate = value;
        break;
      case "markup_rate":
        this.markupRate = value;
        break;
    }

    // Update progress bar
    this.updateSliderProgress(slider);

    // Update calculations
    this.updateCalculations();

    // Update all product Final Price displays when settings change
    this.products.forEach((product) => {
      this.updateProductFinalPrice(product);
    });
  }

  initializeSettingsSliders() {
    // Initialize all settings slider progress bars
    document.querySelectorAll(".em-setting-slider").forEach((slider) => {
      this.updateSliderProgress(slider);

      // Initialize value displays
      const setting = slider.dataset.setting;
      const value = parseFloat(slider.value);
      const valueDisplay = slider
        .closest(".em-slider-container")
        .querySelector(".em-slider-value");

      if (valueDisplay) {
        let displayValue = value;
        if (setting === "machine_price") {
          displayValue = this.currencySymbol + value.toLocaleString();
        } else if (setting === "overhead_rate") {
          displayValue = this.currencySymbol + value;
        } else {
          displayValue = value + "%";
        }
        valueDisplay.textContent = displayValue;
      }
    });
  }

  handleSliderChange(slider) {
    const value = parseFloat(slider.value);
    const valueDisplay = slider
      .closest(".em-slider-container")
      .querySelector(".em-slider-value");

    if (valueDisplay) {
      valueDisplay.textContent = value;
    }

    // Update progress bar
    this.updateSliderProgress(slider);

    // Update calculations
    this.updateCalculations();
  }

  updateProductFromSlider(product, slider) {
    const field = slider.dataset.field;
    const value = parseFloat(slider.value);

    switch (field) {
      case "material_cost":
        product.material_cost = value;
        break;
      case "labor":
        product.labor = value;
        break;
      case "sales":
        product.sales = value;
        break;
    }

    // Update the value display
    const valueDisplay = slider
      .closest(".em-slider-container")
      .querySelector(".em-slider-value");
    if (valueDisplay) {
      valueDisplay.textContent = value;
    }

    // Update progress bar
    this.updateSliderProgress(slider);

    // Update Final Price display for this product
    this.updateProductFinalPrice(product);
  }

  updateProductFinalPrice(product) {
    const calculations = this.calculateROI(product);
    const finalPriceElement = document.getElementById(
      `final-price-${product.id}`,
    );

    if (finalPriceElement) {
      finalPriceElement.textContent = `$${this.formatNumber(calculations.finalPrice, 2)}`;
    }
  }

  initializeSliders() {
    // Initialize all slider progress bars
    document.querySelectorAll(".em-slider").forEach((slider) => {
      this.updateSliderProgress(slider);
    });

    // Initialize all product Final Price displays
    this.products.forEach((product) => {
      this.updateProductFinalPrice(product);
    });
  }

  initializeMobileScrolling() {
    console.log("初始化移动端滚动:", {
      isMobile: this.isMobile,
      screenWidth: window.innerWidth,
    });

    // 监听窗口大小变化
    window.addEventListener("resize", () => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 768;

      console.log("窗口大小变化:", {
        wasMobile,
        isMobile: this.isMobile,
        screenWidth: window.innerWidth,
      });

      // 如果从桌面切换到移动端或反之，重新初始化滚动和产品选择
      if (wasMobile !== this.isMobile) {
        this.updateProductSelectionForDevice();
        this.setupScrollBehavior();
      }
    });

    // 初始化滚动行为
    this.setupScrollBehavior();
  }

  updateProductSelectionForDevice() {
    // 根据当前设备类型更新产品选择
    const defaultSelectionCount = this.isMobile ? 1 : 3;

    // 清空当前选择
    this.selectedProducts.clear();

    // 隐藏所有产品卡片
    this.products.forEach((product) => {
      if (product.element) {
        product.element.style.display = "none";
      }
    });

    // 重新选择默认数量的产品
    const productCards = document.querySelectorAll(
      ".em-product-card-roi[data-product-data]",
    );
    productCards.forEach((card, index) => {
      if (index < defaultSelectionCount) {
        const productId = card.dataset.productId;
        this.selectedProducts.add(productId);
        card.style.display = "block";
      }
    });

    // 同步复选框状态
    this.syncCheckboxStates();

    // 更新计算
    this.updateCalculations();

    console.log(
      `设备切换: ${this.isMobile ? "移动端" : "桌面端"}，已选择 ${defaultSelectionCount} 个产品`,
    );
  }

  setupScrollBehavior() {
    const containers = document.querySelectorAll(".em-products-container");
    console.log("设置滚动行为:", {
      isMobile: this.isMobile,
      containersFound: containers.length,
    });

    containers.forEach((container, index) => {
      console.log(`容器 ${index + 1}:`, {
        display: getComputedStyle(container).display,
        overflowX: getComputedStyle(container).overflowX,
        hasCards: container.querySelectorAll(".em-product-card-roi").length,
      });

      if (this.isMobile) {
        this.enableMobileScrolling(container);
      } else {
        // 在桌面端，不要重置CSS样式，因为CSS媒体查询会处理
        // 只移除移动端特有的事件监听器
        this.disableMobileScrolling(container);
      }
    });
  }

  enableMobileScrolling(container) {
    console.log("启用移动端滚动:", container);

    // 不再强制设置样式，让CSS媒体查询处理
    // 只添加JavaScript功能增强

    // 添加触摸滚动增强
    this.addTouchScrollEnhancement(container);

    // 添加滚动指示器
    this.addScrollIndicators(container);

    // 添加键盘导航支持
    this.addKeyboardNavigation(container);

    // 检查最终样式
    const computedStyle = getComputedStyle(container);
    console.log("容器最终样式:", {
      display: computedStyle.display,
      flexDirection: computedStyle.flexDirection,
      overflowX: computedStyle.overflowX,
      touchAction: computedStyle.touchAction,
    });
  }

  disableMobileScrolling(container) {
    // 不重置CSS样式，只移除JavaScript事件监听器

    // 移除移动端特有的事件监听器
    if (this.handleTouchStart) {
      container.removeEventListener("touchstart", this.handleTouchStart);
      container.removeEventListener("touchmove", this.handleTouchMove);
      container.removeEventListener("touchend", this.handleTouchEnd);
    }

    // 移除滚动指示器
    const indicators = container.querySelectorAll(".scroll-indicator");
    indicators.forEach((indicator) => {
      if (indicator.parentNode) {
        indicator.parentNode.removeChild(indicator);
      }
    });
  }
  addTouchScrollEnhancement(container) {
    let startX = 0;
    let scrollLeft = 0;
    let isScrolling = false;
    let momentum = 0;

    // 存储事件处理器以便后续移除
    this.handleTouchStart = (e) => {
      isScrolling = true;
      startX = e.touches[0].pageX - container.offsetLeft;
      scrollLeft = container.scrollLeft;
      momentum = 0;
      container.style.scrollBehavior = "auto"; // 禁用平滑滚动以获得即时响应
    };

    this.handleTouchMove = (e) => {
      if (!isScrolling) return;
      // 移除 preventDefault，让浏览器处理原生滚动

      const x = e.touches[0].pageX - container.offsetLeft;
      const walk = (x - startX) * 2; // 增加滚动敏感度
      const newScrollLeft = scrollLeft - walk;

      // 计算动量
      momentum = container.scrollLeft - newScrollLeft;
      container.scrollLeft = newScrollLeft;
    };

    this.handleTouchEnd = () => {
      isScrolling = false;
      container.style.scrollBehavior = "smooth"; // 恢复平滑滚动

      // 添加惯性滚动效果
      if (Math.abs(momentum) > 5) {
        const inertiaAnimation = () => {
          container.scrollLeft -= momentum;
          momentum *= 0.95; // 衰减因子

          if (Math.abs(momentum) > 1) {
            requestAnimationFrame(inertiaAnimation);
          }
        };
        requestAnimationFrame(inertiaAnimation);
      }

      // 自动对齐到最近的产品卡片
      this.snapToNearestCard(container);
    };

    container.addEventListener("touchstart", this.handleTouchStart, {
      passive: true,
    });
    container.addEventListener("touchmove", this.handleTouchMove, {
      passive: true,
    });
    container.addEventListener("touchend", this.handleTouchEnd, {
      passive: true,
    });
  }

  snapToNearestCard(container) {
    const cards = container.querySelectorAll(".em-product-card-roi");
    if (cards.length === 0) return;

    const containerRect = container.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;

    let nearestCard = null;
    let minDistance = Infinity;

    cards.forEach((card) => {
      const cardRect = card.getBoundingClientRect();
      const cardCenter = cardRect.left + cardRect.width / 2;
      const distance = Math.abs(cardCenter - containerCenter);

      if (distance < minDistance) {
        minDistance = distance;
        nearestCard = card;
      }
    });

    if (nearestCard) {
      const cardRect = nearestCard.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const targetScrollLeft =
        container.scrollLeft +
        cardRect.left -
        containerRect.left -
        (containerRect.width - cardRect.width) / 2;

      container.scrollTo({
        left: Math.max(0, targetScrollLeft),
        behavior: "smooth",
      });
    }
  }

  addKeyboardNavigation(container) {
    // 添加键盘导航支持
    container.setAttribute("tabindex", "0");

    container.addEventListener("keydown", (e) => {
      if (!this.isMobile) return;

      const cards = container.querySelectorAll(".em-product-card-roi");
      const cardWidth = cards.length > 0 ? cards[0].offsetWidth + 16 : 280; // 包括间距

      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          container.scrollBy({ left: -cardWidth, behavior: "smooth" });
          break;
        case "ArrowRight":
          e.preventDefault();
          container.scrollBy({ left: cardWidth, behavior: "smooth" });
          break;
        case "Home":
          e.preventDefault();
          container.scrollTo({ left: 0, behavior: "smooth" });
          break;
        case "End":
          e.preventDefault();
          container.scrollTo({
            left: container.scrollWidth,
            behavior: "smooth",
          });
          break;
      }
    });
  }

  updateSliderProgress(slider) {
    const sliderWrapper = slider.closest(".em-slider-wrapper");
    const progressBar = sliderWrapper
      ? sliderWrapper.querySelector(".em-slider-progress")
      : null;

    if (progressBar) {
      const min = parseFloat(slider.min);
      const max = parseFloat(slider.max);
      const value = parseFloat(slider.value);

      // 防止除零错误
      if (max > min) {
        const percentage = ((value - min) / (max - min)) * 100;
        progressBar.style.width = percentage + "%";
        console.log(
          `Slider progress updated: min=${min}, max=${max}, value=${value}, percentage=${percentage}%`,
        );
      } else {
        // 如果max等于min，设置进度条为100%
        progressBar.style.width = "100%";
        console.log(
          `Slider progress set to 100%: min=${min}, max=${max}, value=${value}`,
        );
      }
    } else {
      console.log("Slider progress bar not found for slider:", slider);
    }
  }

  addCustomProduct() {
    const nameInput = document.getElementById("em-custom-product-name");
    const priceInput = document.getElementById("em-custom-material-cost");
    const costInput = document.getElementById("em-custom-labor-cost");
    const salesInput = document.getElementById("em-custom-monthly-sales");

    const name = nameInput.value.trim();
    const price = parseFloat(priceInput.value) || 0;
    const cost = parseFloat(costInput.value) || 0;
    const sales = parseInt(salesInput.value) || 0;

    if (!name) {
      this.showNotification("Bitte Produktname eingeben", "error");
      return;
    }

    if (price <= 0) {
      this.showNotification("Bitte einen gültigen Stückpreis eingeben", "error");
      return;
    }

    const product = {
      id: `custom-${Date.now()}`,
      name,
      material_cost: price, // 材料成本
      labor: cost, // 人工成本
      sales,
      type: "custom",
    };

    // Create product card for custom product
    const card = this.createCustomProductCard(product);
    const presetsSection = document.getElementById("preset-products");
    presetsSection.appendChild(card);

    this.products.push(product);
    product.element = card;
    this.bindProductEvents(card, product);

    // Initialize Final Price display for new product
    this.updateProductFinalPrice(product);

    // Add custom product to selected products (use full id for custom products)
    this.selectedProducts.add(product.id);
    card.style.display = "block";

    // Add to mobile visible products list
    this.mobileVisibleProducts.push(product.id);

    // Clear form
    nameInput.value = "";
    priceInput.value = "";
    costInput.value = "";
    salesInput.value = "";

    // Switch to preset view to show the new product
    this.switchToPresetView();
    this.updateCalculations();
    this.showNotification("Produkt erfolgreich hinzugefügt!", "success");

    // On mobile, scroll to the new product
    if (this.isMobile) {
      this.currentMobileProductIndex = this.mobileVisibleProducts.length - 1;
      this.updateMobileProductDisplay();
      this.updateMobileNavigation();
    }

    // 重新初始化滚动行为，因为新增了产品卡片
    setTimeout(() => {
      this.setupScrollBehavior();
      // 初始化新添加的滑块进度条
      const newSliders = card.querySelectorAll(".em-slider");
      newSliders.forEach((slider) => {
        this.updateSliderProgress(slider);
      });
    }, 200);
  }

  createCustomProductCard(product) {
    const card = document.createElement("div");
    card.className = "em-product-card-roi";
    card.dataset.productId = product.id;

    // Calculate max values based on input value directly
    const priceMax = Math.max(Math.ceil(product.material_cost * 1.5), 10);
    const costMax = Math.max(Math.ceil(product.labor * 1.5), 10);
    const salesMax = Math.max(Math.ceil(product.sales * 1.5), 10);

    // Set min values to allow proper range
    const priceMin = 0;
    const costMin = 0;
    const salesMin = 0;

    // Calculate initial progress
    const priceProgress =
      priceMax > priceMin
        ? ((product.material_cost - priceMin) / (priceMax - priceMin)) * 100
        : 100;
    const costProgress =
      costMax > costMin
        ? ((product.labor - costMin) / (costMax - costMin)) * 100
        : 100;
    const salesProgress =
      salesMax > salesMin
        ? ((product.sales - salesMin) / (salesMax - salesMin)) * 100
        : 100;

    card.innerHTML = `
      <div class="em-product-card__header">
        <div class="em-product-card-roi__image em-product-card-roi__image--placeholder">
          <span class="em-product-icon">📦</span>
        </div>
        <h3 class="em-product-card__name">${product.name}</h3>
        <button type="button" class="em-product-card__remove" onclick="emROICalculator.removeProductCard(this)">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6"/>
          </svg>
        </button>
      </div>

      <div class="em-product-card__final-price">
        <div class="em-final-price-label">Endpreis</div>
        <div class="em-final-price-value" id="final-price-${product.id}">${this.currencySymbol}0.00</div>
      </div>

      <div class="em-product-card__controls">
        <div class="em-control-group">
          <label class="em-control-label">Materialkosten(${this.currencySymbol})</label>
          <div class="em-slider-container">
            <div class="em-slider-wrapper">
              <div class="em-slider-progress" style="width: ${priceProgress}%"></div>
              <input type="range" class="em-slider em-material-cost-slider" min="${priceMin}" max="${priceMax}" value="${product.material_cost}" data-field="material_cost">
            </div>
            <div class="em-slider-value">${product.material_cost}</div>
          </div>
        </div>

        <div class="em-control-group">
          <label class="em-control-label">Arbeitskosten(${this.currencySymbol})</label>
          <div class="em-slider-container">
            <div class="em-slider-wrapper">
              <div class="em-slider-progress" style="width: ${costProgress}%"></div>
              <input type="range" class="em-slider em-labor-cost-slider" min="${costMin}" max="${costMax}" value="${product.labor}" data-field="labor">
            </div>
            <div class="em-slider-value">${product.labor}</div>
          </div>
        </div>

        <div class="em-control-group">
          <label class="em-control-label">Monatliches Verkaufsvolumen</label>
          <div class="em-slider-container">
            <div class="em-slider-wrapper">
              <div class="em-slider-progress" style="width: ${salesProgress}%"></div>
              <input type="range" class="em-slider em-monthly-sales-slider" min="${salesMin}" max="${salesMax}" value="${product.sales}" data-field="sales">
            </div>
            <div class="em-slider-value">${product.sales}</div>
          </div>
        </div>
      </div>
    `;

    return card;
  }

  switchToPresetView() {
    // Switch to preset tab
    document.querySelectorAll(".em-category-tab").forEach((tab) => {
      tab.classList.remove("active");
      if (tab.dataset.category === "preset") {
        tab.classList.add("active");
      }
    });

    // Show preset section
    document
      .querySelectorAll(
        ".em-preset-products-section, .em-custom-products-section",
      )
      .forEach((section) => {
        section.classList.remove("active");
        section.style.display = "none";
      });

    const presetSection = document.getElementById("em-preset-products");
    if (presetSection) {
      presetSection.classList.add("active");
      presetSection.style.display = "block";
    }

    this.currentCategory = "preset";
  }

  removeProductCard(button) {
    const card = button.closest(".em-product-card-roi");
    const productId = card.dataset.productId;

    // Check if it's a custom product
    const isCustomProduct = productId.startsWith("custom-");

    // Remove from products array
    this.products = this.products.filter((product) => product.id !== productId);

    // Remove from selected products set
    if (isCustomProduct) {
      // Custom products use full id
      this.selectedProducts.delete(productId);
      // Remove from mobileVisibleProducts
      const mobileIndex = this.mobileVisibleProducts.indexOf(productId);
      if (mobileIndex > -1) {
        this.mobileVisibleProducts.splice(mobileIndex, 1);
      }
    } else {
      // Preset products - try both with and without prefix
      const actualProductId = productId.replace("preset-", "");
      this.selectedProducts.delete(actualProductId);
      this.selectedProducts.delete(productId);
      // Remove from mobileVisibleProducts
      const mobileIndex = this.mobileVisibleProducts.indexOf(actualProductId);
      if (mobileIndex > -1) {
        this.mobileVisibleProducts.splice(mobileIndex, 1);
      }
    }

    // Update checkbox in modal if it exists (only for preset products)
    if (!isCustomProduct) {
      const actualProductId = productId.replace("preset-", "");
      const modalCheckbox = document.querySelector(
        `.em-product-checkbox[data-product-id="${actualProductId}"]`,
      );
      if (modalCheckbox) {
        modalCheckbox.checked = false;
      }
    }

    // Remove the card from DOM for custom products, hide for preset
    if (isCustomProduct) {
      card.remove();
    } else {
      card.style.display = "none";
    }

    // Update mobile navigation after removing product
    if (this.isMobile) {
      // Adjust current index if needed
      if (this.currentMobileProductIndex >= this.mobileVisibleProducts.length) {
        this.currentMobileProductIndex = Math.max(
          0,
          this.mobileVisibleProducts.length - 1,
        );
      }
      this.updateMobileProductDisplay();
      this.updateMobileNavigation();
    }

    this.updateCalculations();
    this.showNotification("Product removed", "info");

    // 重新初始化滚动行为，因为移除了产品卡片
    setTimeout(() => {
      this.setupScrollBehavior();
    }, 100);
  }

  calculateROI(product) {
    // 新的计算公式: Final Price = (Material Cost + Labor + Machine Depreciation + Overhead) × (1 + Markup)
    const machineDepreciationPerUnit =
      this.machinePrice * (this.machineDepreciationRate / 100);
    const overheadPerUnit = this.overheadRate; // Overhead现在是固定金额，不是百分比
    const finalPrice =
      (product.material_cost +
        product.labor +
        machineDepreciationPerUnit +
        overheadPerUnit) *
      (1 + this.markupRate / 100);

    const monthlyRevenue = finalPrice * product.sales;
    const monthlyCost = (product.material_cost + product.labor) * product.sales;
    const monthlyProfit = monthlyRevenue - monthlyCost;

    // 产品投资包括材料成本和人工成本
    const productInvestment = monthlyCost;

    const paybackMonths =
      productInvestment > 0 && monthlyProfit > 0
        ? productInvestment / monthlyProfit
        : 0;

    const annualROI =
      productInvestment > 0
        ? ((monthlyProfit * 12) / productInvestment) * 100
        : 0;

    return {
      monthlyRevenue,
      monthlyProfit,
      paybackMonths, // 产品本身的回本时间
      annualROI: Math.max(0, annualROI),
      annualRevenue: monthlyRevenue * 12,
      annualNetProfit: monthlyProfit * 12,
      productInvestment, // 产品投资，不包含机器成本
      finalPrice, // 最终售价
      machineDepreciationPerUnit, // 单位机器折旧
      overheadPerUnit, // 单位管理费用
    };
  }

  updateCalculations() {
    // Get only selected products
    const selectedProducts = this.products.filter((product) => {
      // For preset products, check without the 'preset-' prefix
      // For custom products, check with the full id
      if (product.type === "custom") {
        return this.selectedProducts.has(product.id);
      } else {
        const productId = product.id.replace("preset-", "");
        return this.selectedProducts.has(productId);
      }
    });

    if (selectedProducts.length === 0) {
      this.updateForecastDisplay(0, 0, 0, 0, 0, 0);
      this.updateChart([]);
      return;
    }

    // Calculate totals for selected products only
    let totalAnnualRevenue = 0;
    let totalAnnualProfit = 0;
    let totalProductInvestment = 0; // 仅产品投资
    let totalMonthlyProfit = 0;
    let totalMonthlyRevenue = 0;
    let totalFinalPrice = 0;
    let totalSalesVolume = 0;

    const chartData = [];

    selectedProducts.forEach((product) => {
      const calculations = this.calculateROI(product);

      totalAnnualRevenue += calculations.annualRevenue;
      totalAnnualProfit += calculations.annualNetProfit;
      totalProductInvestment += calculations.productInvestment; // 使用计算结果中的产品投资
      totalMonthlyProfit += calculations.monthlyProfit;
      totalMonthlyRevenue += calculations.monthlyRevenue;
      totalFinalPrice += calculations.finalPrice * product.sales; // 总售价
      totalSalesVolume += product.sales;

      // Prepare chart data
      chartData.push({
        product: product.name,
        monthlyProfit: calculations.monthlyProfit,
        cumulativeProfit: 0, // Will be calculated below
      });
    });

    // Calculate average final price per unit
    const averageFinalPrice =
      totalSalesVolume > 0 ? totalFinalPrice / totalSalesVolume : 0;

    // Calculate total investment (产品投资 + 机器成本只计算一次)
    const totalInvestment = totalProductInvestment + this.machinePrice;

    // Calculate average payback period (包含机器成本)
    const averagePayback =
      totalInvestment > 0 && totalMonthlyProfit > 0
        ? totalInvestment / totalMonthlyProfit
        : 0;

    // Calculate machine payback period (机器单独回本时间)
    const machinePayback =
      totalMonthlyProfit > 0 ? this.machinePrice / totalMonthlyProfit : 0;

    // Update display
    this.updateForecastDisplay(
      averagePayback,
      totalAnnualRevenue,
      totalAnnualProfit,
      machinePayback,
      averageFinalPrice,
      totalMonthlyRevenue,
    );
    this.updateChart(chartData);
  }

  updateForecastDisplay(
    paybackPeriod,
    annualRevenue,
    annualProfit,
    machinePayback,
    averageFinalPrice,
    monthlyRevenue,
  ) {
    const paybackElement = document.getElementById("em-payback-period");
    const revenueElement = document.getElementById("em-annual-revenue");
    const profitElement = document.getElementById("em-annual-profit");
    const machinePaybackElement = document.getElementById(
      "em-machine-payback-period",
    );
    const finalPriceElement = document.getElementById("em-final-price");
    const monthlyRevenueElement = document.getElementById("em-monthly-revenue");

    // Update Final Price (primary metric)
    if (finalPriceElement) {
      finalPriceElement.textContent = `${this.currencySymbol}${this.formatNumber(averageFinalPrice, 2)}`;
    }

    // Update Monthly Revenue
    if (monthlyRevenueElement) {
      monthlyRevenueElement.textContent = `${this.currencySymbol}${this.formatNumber(monthlyRevenue, 0)}`;
    }

    if (paybackElement) {
      const paybackText =
        paybackPeriod > 0
          ? paybackPeriod < 1
            ? paybackPeriod.toFixed(1)
            : paybackPeriod.toFixed(1)
          : "∞";
      paybackElement.innerHTML = `${paybackText}<span class="em-metric-unit">Monat(e)</span>`;
    }

    // 显示机器单独的回本时间
    if (machinePaybackElement) {
      const machinePaybackText =
        machinePayback > 0
          ? machinePayback < 1
            ? machinePayback.toFixed(1)
            : machinePayback.toFixed(1)
          : "∞";
      machinePaybackElement.innerHTML = `${machinePaybackText}<span class="em-metric-unit">Monat(e)</span>`;
    }

    if (revenueElement) {
      revenueElement.textContent = `${this.currencySymbol}${this.formatNumber(annualRevenue, 0)}`;
    }

    if (profitElement) {
      profitElement.textContent = `${this.currencySymbol}${this.formatNumber(annualProfit, 0)}`;
    }
  }

  initializeChart() {
    const canvas = document.getElementById("em-profit-chart");
    if (!canvas) return;

    this.chart = new EMChart(canvas, {
      type: "line",
      data: {
        labels: [],
        datasets: [
          {
            label: "Kumulierter Gewinn",
            data: [],
            borderColor: "#ff9500",
            backgroundColor: "rgba(255, 149, 0, 0.1)",
            borderWidth: 3,
            fill: true,
            tension: 0.4,
            pointBackgroundColor: "#ff9500",
            pointBorderColor: "#fff",
            pointBorderWidth: 2,
            pointRadius: 6,
          },
        ],
      },
      currencySymbol: this.currencySymbol,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: "index",
        },
        plugins: {
          legend: {
            display: true,
            position: "bottom",
            labels: {
              color: "#a0a0a0",
              font: {
                size: 12,
              },
              usePointStyle: true,
              padding: 20,
            },
          },
        },
        scales: {
          x: {
            grid: {
              color: "#4a4a4a",
              borderColor: "#4a4a4a",
            },
            ticks: {
              color: "#a0a0a0",
              font: {
                size: 11,
              },
            },
          },
          y: {
            grid: {
              color: "#4a4a4a",
              borderColor: "#4a4a4a",
            },
            ticks: {
              color: "#a0a0a0",
              font: {
                size: 11,
              },
              callback: (value) => {
                return (
                  this.currencySymbol +
                  (value >= 1000 ? (value / 1000).toFixed(1) + "k" : value)
                );
              },
            },
          },
        },
      },
    });
  }

  updateChart(data) {
    if (!this.chart) return;

    // Generate 12 months of data
    const months = [
      "M1",
      "M2",
      "M3",
      "M4",
      "M5",
      "M6",
      "M7",
      "M8",
      "M9",
      "M10",
      "M11",
      "M12",
    ];

    const monthlyProfits = [];
    const cumulativeProfits = [];

    let totalMonthlyProfit = 0;
    data.forEach((item) => {
      totalMonthlyProfit += item.monthlyProfit;
    });

    for (let i = 0; i < 12; i++) {
      monthlyProfits.push(totalMonthlyProfit);
      cumulativeProfits.push(totalMonthlyProfit * (i + 1));
    }

    this.chart.data.labels = months;
    this.chart.data.datasets[0].data = cumulativeProfits;
    this.chart.update("none"); // No animation for better performance
  }

  showNotification(message, type = "info") {
    // Create notification element
    const notification = document.createElement("div");
    notification.className = `em-notification em-notification--${type}`;
    notification.textContent = message;

    // Style the notification
    Object.assign(notification.style, {
      position: "fixed",
      top: "20px",
      right: "20px",
      padding: "12px 20px",
      borderRadius: "8px",
      color: "#fff",
      fontSize: "14px",
      fontWeight: "500",
      zIndex: "1000",
      maxWidth: "300px",
      opacity: "0",
      transform: "translateX(100%)",
      transition: "all 0.3s ease",
    });

    // Set background color based on type
    const colors = {
      success: "#27ae60",
      error: "#e74c3c",
      info: "#3498db",
      warning: "#f39c12",
    };
    notification.style.backgroundColor = colors[type] || colors.info;

    document.body.appendChild(notification);

    // Animate in
    setTimeout(() => {
      notification.style.opacity = "1";
      notification.style.transform = "translateX(0)";
    }, 100);

    // Remove after delay
    setTimeout(() => {
      notification.style.opacity = "0";
      notification.style.transform = "translateX(100%)";
      setTimeout(() => {
        if (notification.parentElement) {
          notification.parentElement.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }

  formatNumber(number, decimals = 2) {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(number);
  }

  formatCurrency(amount, currency = "USD") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  }
}

// Initialize the calculator when DOM is loaded
let emROICalculator;

document.addEventListener("DOMContentLoaded", function () {
  // Wait for Chart library to load
  if (typeof Chart !== "undefined") {
    emROICalculator = new EMROICalculator();
  } else {
    // Fallback if Chart.js isn't loaded yet
    setTimeout(() => {
      emROICalculator = new EMROICalculator();
    }, 500);
  }
});

// Export for global access
window.emROICalculator = emROICalculator;
